import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { CalendarDays, Layers, ListChecks } from "lucide-react";

export default function InvitePage() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", weight_kg: "", height_cm: "", allergies: "", injuries: "", medications: "", goals: "", experience: "", availability: "", equipment: "", privacy_accepted: false });
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch(() => setError("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.privacy_accepted) {
      setFormError(t("field.privacyRequired"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-invite", {
        token, ...form,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        availability: form.availability ? Number(form.availability) : null,
      });
      setUser(data);
      navigate("/app");
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="kc-card p-8 max-w-md text-center" data-testid="invite-invalid">
          <img src="/adtraining-mark-t.png" alt="AD" className="h-10 w-auto mx-auto mb-3" />
          <p className="text-muted-foreground">{t("invite.invalid")}</p>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" data-testid="invite-loading" />
      </div>
    );
  }

  const p = invite.program;

  return (
    <div className="min-h-screen py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="invite-page">
        <div className="text-center">
          <img src="/adtraining-full-t.png" alt="AD TRAINING PROGRAM" className="h-28 w-auto mx-auto mb-4 kc-logo-ondark" />
          <img src="/adtraining-full-onlight.png" alt="AD TRAINING PROGRAM" className="h-28 w-auto mx-auto mb-4 kc-logo-onlight" />
          <p className="kc-eyebrow">{t("invite.title")}</p>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mt-1" data-testid="invite-coach-name">
            {invite.coach_name}
          </h1>
          {invite.label && <p className="text-muted-foreground mt-2">{invite.label}</p>}
        </div>

        {p && (
          <div className="kc-card p-6 space-y-5" data-testid="invite-program-card">
            <div>
              <p className="kc-eyebrow mb-1">{t("invite.program")}</p>
              <h2 className="text-2xl font-bold">{p.name}</h2>
              {p.description && <p className="text-muted-foreground text-sm mt-1">{p.description}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-secondary/50 rounded-lg p-4">
                <Layers className="w-5 h-5 text-accent mb-2" />
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("invite.planDetails")}</p>
                <p className="font-bold">{p.phases_count} {t("invite.phases")}</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4">
                <CalendarDays className="w-5 h-5 text-accent mb-2" />
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("invite.currentPhase")}</p>
                <p className="font-bold" data-testid="invite-current-phase">{p.current_phase?.name || "—"}</p>
              </div>
            </div>
            {p.current_phase?.goal && (
              <p className="text-sm text-muted-foreground">{t("client.goal")}: {p.current_phase.goal}</p>
            )}
            {p.weeks.length > 0 && (
              <div>
                <p className="kc-eyebrow mb-2 flex items-center gap-2"><ListChecks className="w-4 h-4" />{t("invite.weeks")}</p>
                <ul className="divide-y divide-border" data-testid="invite-weeks-list">
                  {p.weeks.map((w) => (
                    <li key={w.id} className="py-2 flex justify-between text-sm">
                      <span>{w.month} — {w.name}</span>
                      <span className="text-muted-foreground">{w.routines} {t("invite.routines")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {invite.valid ? (
          <form onSubmit={submit} className="kc-card p-6 space-y-4" data-testid="invite-register-form">
            <h3 className="text-xl font-bold">{t("invite.register")}</h3>
            <input data-testid="invite-name-input" required placeholder={t("register.name")} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="kc-input" />
            <input data-testid="invite-email-input" type="email" required placeholder={t("login.email")} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="kc-input" />
            <input data-testid="invite-password-input" type="password" required minLength={6} placeholder={t("login.password")}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="kc-input" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="invite-weight-input" type="number" min="0" step="0.1" placeholder={t("field.weight")}
                value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="kc-input" />
              <input data-testid="invite-height-input" type="number" min="0" step="1" placeholder={t("field.height")}
                value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="kc-input" />
            </div>
            <textarea data-testid="invite-allergies-input" rows={2} placeholder={t("field.allergies")}
              value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className="kc-input" />
            <textarea data-testid="invite-injuries-input" rows={2} placeholder={t("field.injuries")}
              value={form.injuries} onChange={(e) => setForm({ ...form, injuries: e.target.value })} className="kc-input" />
            <textarea data-testid="invite-medications-input" rows={2} placeholder={t("field.medications")}
              value={form.medications} onChange={(e) => setForm({ ...form, medications: e.target.value })} className="kc-input" />
            <textarea data-testid="invite-goals-input" rows={2} placeholder={t("q.goals")}
              value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} className="kc-input" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="invite-experience-select" value={form.experience}
                onChange={(e) => setForm({ ...form, experience: e.target.value })} className="kc-input">
                <option value="">{t("q.experience")}</option>
                <option value="beginner">{t("q.expBeginner")}</option>
                <option value="intermediate">{t("q.expIntermediate")}</option>
                <option value="advanced">{t("q.expAdvanced")}</option>
              </select>
              <input data-testid="invite-availability-input" type="number" min="1" max="7" placeholder={t("q.availability")}
                value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="kc-input" />
            </div>
            <input data-testid="invite-equipment-input" placeholder={t("q.equipment")} value={form.equipment}
              onChange={(e) => setForm({ ...form, equipment: e.target.value })} className="kc-input" />
            <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer" data-testid="privacy-consent-label">
              <input data-testid="privacy-checkbox" type="checkbox" checked={form.privacy_accepted}
                onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })}
                className="mt-0.5 w-5 h-5 accent-[#C9A86A]" />
              <span>
                {t("field.privacy")}{" "}
                <a data-testid="privacy-policy-link" href="/privacy" target="_blank" rel="noreferrer" className="text-primary underline">
                  {t("field.privacyLink")}
                </a>
              </span>
            </label>
            {formError && <p data-testid="invite-form-error" className="text-sm text-destructive">{formError}</p>}
            <button data-testid="invite-submit-button" type="submit" disabled={loading} className="kc-btn-primary w-full">
              {loading ? t("common.loading") : t("invite.start")}
            </button>
          </form>
        ) : (
          <div className="kc-card p-6 text-center text-muted-foreground" data-testid="invite-used-notice">
            {t("invite.invalid")}
          </div>
        )}
      </div>
    </div>
  );
}
